import React from 'react';
import { type StudioPageContent, type StudioWatermarkLogoId } from '../../../../constants';
import { colorWithAlpha, isDarkColor, mixColor } from '../../../../utils/color';
import { STUDIO_FONT_SC, STUDIO_FONT_TC } from '../../../../utils/fonts';
import { CanvasLogoWatermark } from '../../../LogoWatermark';
import StudioPageNumber from '../../StudioPageNumber';

export interface SpotlightTokens {
  ink: string;
  muted: string;
  accent: string;
  onAccent: string;
  panel: string;
  line: string;
  positive: string;
  negative: string;
  sans: string;
}

export interface SpotlightInjectedProps {
  tokens?: SpotlightTokens;
  isStory?: boolean;
  locale?: string;
  qrCodeUrl?: string;
}

export interface SpotlightLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
  pageNumber?: number;
  pageCount?: number;
  watermarkLogo?: StudioWatermarkLogoId | null;
  showChubbDisclaimer?: boolean;
  qrCodeUrl?: string;
  pinContentTop?: boolean;
}

interface SpotlightFrameProps extends SpotlightLayoutProps {
  children: React.ReactElement;
}

const SpotlightFrame: React.FC<SpotlightFrameProps> = ({
  width,
  height,
  format,
  locale = 'zh-Hant-HK',
  themeColor = '#1456f0',
  profileName,
  pageNumber = 1,
  pageCount = 7,
  watermarkLogo,
  showChubbDisclaimer = false,
  qrCodeUrl,
  pinContentTop = false,
  children
}) => {
  const isStory = format === 'story';
  const accent = isDarkColor(themeColor) ? mixColor(themeColor, 'white', 0.28) : themeColor;
  const tokens: SpotlightTokens = {
    ink: '#ffffff',
    muted: 'rgba(255,255,255,0.67)',
    accent,
    onAccent: isDarkColor(accent) ? '#ffffff' : '#07100c',
    panel: 'rgba(255,255,255,0.075)',
    line: 'rgba(255,255,255,0.16)',
    positive: '#63f6b3',
    negative: '#ff8090',
    sans: `'DM Sans', '${STUDIO_FONT_TC}', '${STUDIO_FONT_SC}', -apple-system, BlinkMacSystemFont, sans-serif`
  };
  const note = profileName && String(profileName).trim() ? String(profileName).trim() : '';

  return (
    <div
      style={{
        width,
        height,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        color: tokens.ink,
        background: `radial-gradient(circle at 88% 12%, ${colorWithAlpha(
          accent,
          0.34
        )} 0%, transparent 38%), linear-gradient(145deg, #11151c 0%, #05070a 72%)`,
        fontFamily: tokens.sans,
        // Pin alignment so the page renders identically no matter what the host
        // is (the picker cover lives inside a MUI Button whose text-align:center
        // would otherwise centre the text).
        textAlign: 'left'
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.12,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.16) 1px, transparent 1px)',
          backgroundSize: isStory ? '42px 42px' : '34px 34px',
          pointerEvents: 'none'
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: isStory ? -68 : -54,
          right: isStory ? -58 : -46,
          width: isStory ? 205 : 165,
          height: isStory ? 205 : 165,
          border: `34px solid ${colorWithAlpha(accent, 0.18)}`,
          borderRadius: '50%'
        }}
      />

      <CanvasLogoWatermark logo={watermarkLogo} color="#ffffff" isStory={isStory} />
      <StudioPageNumber
        pageNumber={pageNumber}
        pageCount={pageCount}
        color={tokens.accent}
        isStory={isStory}
      />

      <header
        style={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: isStory ? '27px 29px 0' : '21px 27px 0',
          color: tokens.ink,
          fontSize: isStory ? 9.5 : 8,
          fontWeight: 800
        }}
      >
        <span
          style={{
            maxWidth: isStory ? 220 : 190,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: 0.3,
            textTransform: 'none'
          }}
        >
          {note}
        </span>
      </header>

      <main
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: isStory
            ? `30px 29px ${pinContentTop ? 116 : showChubbDisclaimer ? 50 : 22}px`
            : `22px 27px ${pinContentTop ? 80 : showChubbDisclaimer ? 44 : 15}px`,
          boxSizing: 'border-box',
          overflowWrap: 'anywhere'
        }}
      >
        {React.cloneElement(children as React.ReactElement<any>, { tokens, isStory, locale, qrCodeUrl })}
      </main>
    </div>
  );
};

export default SpotlightFrame;
